import { IOfferDescriptionItem } from 'models/offer-description-item.model';

import { OfferStatusEnum } from './enums/offer-status.enum';
import { PricingUnitEnum } from './enums/pricing-unit.enum';
import { PricingItemScope } from './enums/pricing-item-scope.enum';
import { RoundedPriceEnum } from './enums/rounded-price.enum';
import { FuelIndexing } from './fuel-indexing.model';
import { LIST_SERVICE_TYPE, ServiceType } from './offer-service-type.model';
import { OfferServiceTypeEnum } from './enums/offer-service-type.enum';
import { ColorEnum } from './enums/color.enum';
import { LIST_MARCHANDISE_TYPE, MerchandiseType } from './offer-merchandise-type.model';
import { OfferMerchandiseTypeEnum } from './enums/offer-merchandise-type.enum';
import { Transporter } from './transporter.model';

export class Offer {
  public id?: number;
  public alias?: string;
  public name?: string;
  public description?: string;
  public flexibilityDeadline?: number;
  public status: OfferStatus;
  public measureUnit?: PricingUnitEnum;
  public itemScope?: PricingItemScope;
  public roundedUpPrice: boolean = false;
  public pickupAddressFloorNumber: boolean = false;
  public pickupComments: boolean = false;
  public pickupPhoneCallAnHourBefore: boolean = false;
  public pickupFloorUpstairQuotationNeeded: boolean = false;
  public pickupVatExoneration: boolean = false;
  public pickupTimeslotStart?: number;
  public pickupTimeslotStartDefault?: number;
  public pickupTimeslotEnd?: number;
  public pickupTimeslotEndDefault?: number;
  public pickupTimeslotRange?: number;
  public deliveryAddressFloorNumber: boolean = false;
  public deliveryComments: boolean = false;
  public deliveryPhoneCallAnHourBefore: boolean = false;
  public deliveryFloorUpstairQuotationNeeded: boolean = false;
  public deliveryVatExoneration: boolean = false;
  public deliveryTimeslotStart?: number;
  public deliveryTimeslotStartDefault?: number;
  public deliveryTimeslotEnd?: number;
  public deliveryTimeslotEndDefault?: number;
  public deliveryTimeslotRange?: number;
  public roundedPrice?: RoundedPriceEnum;
  public offerDescriptionItems: IOfferDescriptionItem[] = [];
  public productCode?: string;
  public weightingFactor?: number;
  public carbonEmissionFactor?: number;
  public fuelIndexing?: FuelIndexing;
  public transporter!: Transporter;
  public merchandiseType: MerchandiseType;
  public serviceType: ServiceType;
  public isControlledTemperature?: boolean;
  public isHazardousMaterial?: boolean;
  public isQuotation: boolean = false;
  public minTemperatureThreshold?: number;
  public maxTemperatureThreshold?: number;

  constructor(data: any = {}) {
    Object.assign(this, data);
    this.fuelIndexing = data.fuelIndexing ? new FuelIndexing(data.fuelIndexing) : undefined;
    this.status = LIST_OFFER_STATUS[data.status || OfferStatusEnum.INACTIVE];
    this.merchandiseType = LIST_MARCHANDISE_TYPE[data.merchandiseType || OfferMerchandiseTypeEnum.CONDITIONED];
    this.serviceType = LIST_SERVICE_TYPE[data.serviceType || OfferServiceTypeEnum.LTL];
    this.transporter = new Transporter(data.transporter);
  }

  public serialize(): any {
    return {
      id: this.id,
      alias: this.alias,
      name: this.name,
      description: this.description,
      flexibilityDeadline: this.flexibilityDeadline,
      status: this.status?.code,
      measureUnit: this.measureUnit,
      itemScope: this.itemScope,
      roundedUpPrice: this.roundedUpPrice,
      pickupAddressFloorNumber: this.pickupAddressFloorNumber,
      pickupComments: this.pickupComments,
      pickupPhoneCallAnHourBefore: this.pickupPhoneCallAnHourBefore,
      pickupFloorUpstairQuotationNeeded: this.pickupFloorUpstairQuotationNeeded,
      pickupVatExoneration: this.pickupVatExoneration,
      pickupTimeslotStart: this.pickupTimeslotStart,
      pickupTimeslotStartDefault: this.pickupTimeslotStartDefault,
      pickupTimeslotEnd: this.pickupTimeslotEnd,
      pickupTimeslotEndDefault: this.pickupTimeslotEndDefault,
      pickupTimeslotRange: this.pickupTimeslotRange,
      deliveryAddressFloorNumber: this.deliveryAddressFloorNumber,
      deliveryComments: this.deliveryComments,
      deliveryPhoneCallAnHourBefore: this.deliveryPhoneCallAnHourBefore,
      deliveryFloorUpstairQuotationNeeded: this.deliveryFloorUpstairQuotationNeeded,
      deliveryVatExoneration: this.deliveryVatExoneration,
      deliveryTimeslotStart: this.deliveryTimeslotStart,
      deliveryTimeslotStartDefault: this.deliveryTimeslotStartDefault,
      deliveryTimeslotEnd: this.deliveryTimeslotEnd,
      deliveryTimeslotEndDefault: this.deliveryTimeslotEndDefault,
      deliveryTimeslotRange: this.deliveryTimeslotRange,
      roundedPrice: this.roundedPrice,
      offerDescriptionItems: this.offerDescriptionItems,
      productCode: this.productCode,
      weightingFactor: this.weightingFactor ? Math.round(this.weightingFactor * 10) / 10 : this.weightingFactor,
      carbonEmissionFactor: this.carbonEmissionFactor,
      fuelIndexing: this.fuelIndexing ? this.fuelIndexing.serialize() : undefined,
      transporter: this.transporter,
      merchandiseType: this.merchandiseType?.code,
      serviceType: this.serviceType?.code,
      isControlledTemperature: this.isControlledTemperature,
      isHazardousMaterial: this.isHazardousMaterial,
      isQuotation: this.isQuotation,
      minTemperatureThreshold: this.minTemperatureThreshold,
      maxTemperatureThreshold: this.maxTemperatureThreshold,
    };
  }

  public isUnconditionedOffer(): boolean {
    return this.merchandiseType.code === OfferMerchandiseTypeEnum.UNCONDITIONED;
  }

  public isLTLOffer(): boolean {
    return this.serviceType.code === OfferServiceTypeEnum.LTL;
  }
}

export class OfferStatus {
  public code!: OfferStatusEnum;
  public labelKey!: string;
  public color!: ColorEnum;
  public emoji!: string;

  constructor(data = {}) {
    Object.assign(this, data);
  }
}

export const LIST_OFFER_STATUS: { [key: string]: OfferStatus } = {
  INACTIVE: new OfferStatus({ code: OfferStatusEnum.INACTIVE, labelKey: 'DISABLED_FEM', color: ColorEnum.DANGER, emoji: '❌' }),
  ACTIVE: new OfferStatus({ code: OfferStatusEnum.ACTIVE, labelKey: 'ENABLED_FEM', color: ColorEnum.SUCCESS, emoji: '✅️' }),
};
